import { ago } from "../time";
import type { ConnectedAccount, EndUser } from "../types/account";

/**
 * The connected accounts. Thirteen accounts across nine providers,
 * deliberately spread across every status: most connected, one syncing, two needing the
 * user, one paused, one disconnected. The dashboard is only interesting when
 * the unhappy paths are on screen.
 *
 * No account carries a `proxy`. The field stays on the type because the
 * deferred channels need it, but nothing in the launch catalog uses egress
 * pinning and a screenshot should not imply otherwise.
 */
export const ACCOUNTS: ConnectedAccount[] = [
  {
    id: "acc_gm_maya",
    provider: "gmail",
    handle: "maya@northwind.io",
    providerId: "maya@northwind.io",
    endUserId: "u_maya",
    status: "connected",
    auth: "oauth",
    connectedAt: ago(18, "w"),
    lastSyncAt: ago(40, "s"),
    budget: {
      windows: [
        { label: "Sends", used: 412, limit: 2000, resetsAt: ago(-10, "h") },
        { label: "Recipients", used: 1180, limit: 10000, resetsAt: ago(-10, "h") },
      ],
    },
    stats: { chats: 184, unread: 7, sent: 412, received: 2338 },
  },
  {
    id: "acc_gc_maya",
    provider: "google-calendar",
    handle: "maya@northwind.io",
    providerId: "maya@northwind.io",
    endUserId: "u_maya",
    status: "connected",
    auth: "oauth",
    connectedAt: ago(18, "w"),
    lastSyncAt: ago(12, "s"),
    budget: {
      windows: [{ label: "API units", used: 96, limit: 600, resetsAt: ago(-1, "h") }],
    },
    stats: { chats: 0, unread: 0, sent: 38, received: 212 },
  },
  {
    id: "acc_ic_tom",
    provider: "icloud",
    handle: "tom.okafor@icloud.com",
    providerId: "tom.okafor@icloud.com",
    endUserId: "u_tom",
    status: "needs_attention",
    auth: "imap",
    connectedAt: ago(9, "w"),
    lastSyncAt: ago(3, "h"),
    attention: {
      code: "password_changed",
      message:
        "Apple revoked this app password when the Apple Account password changed. Generating a new one reconnects the mailbox.",
      since: ago(3, "h"),
      selfServe: true,
    },
    budget: {
      windows: [{ label: "Sends", used: 18, limit: 1000, resetsAt: ago(-10, "h") }],
    },
    stats: { chats: 77, unread: 12, sent: 96, received: 1142 },
  },
  {
    id: "acc_gm_tom",
    provider: "gmail",
    handle: "tom@northwind.io",
    providerId: "tom@northwind.io",
    endUserId: "u_tom",
    status: "connected",
    auth: "oauth",
    connectedAt: ago(14, "w"),
    lastSyncAt: ago(55, "s"),
    budget: {
      windows: [{ label: "Sends", used: 148, limit: 2000, resetsAt: ago(-10, "h") }],
    },
    stats: { chats: 0, unread: 21, sent: 388, received: 1904 },
  },
  {
    id: "acc_fm_lena",
    provider: "fastmail",
    handle: "lena@fischer.dev",
    providerId: "lena@fischer.dev",
    endUserId: "u_lena",
    status: "connected",
    auth: "imap",
    connectedAt: ago(7, "w"),
    lastSyncAt: ago(8, "s"),
    budget: {
      windows: [{ label: "Sends", used: 71, limit: 2000, resetsAt: ago(-10, "h") }],
    },
    stats: { chats: 41, unread: 2, sent: 219, received: 604 },
  },
  {
    id: "acc_ol_lena",
    provider: "outlook",
    handle: "lena.fischer@northwind.io",
    providerId: "lena.fischer@northwind.io",
    endUserId: "u_lena",
    status: "syncing",
    auth: "oauth",
    connectedAt: ago(26, "m"),
    lastSyncAt: ago(30, "s"),
    backfill: {
      progress: 0.37,
      reachedAt: ago(62, "w"),
      chatsImported: 38,
      messagesImported: 14280,
    },
    budget: {
      windows: [{ label: "Sends", used: 0, limit: 1500, resetsAt: ago(-10, "h") }],
    },
    stats: { chats: 38, unread: 0, sent: 0, received: 14280 },
  },
  {
    id: "acc_zo_dan",
    provider: "zoho",
    handle: "dan@whitfield.co",
    providerId: "dan@whitfield.co",
    endUserId: "u_dan",
    status: "paused",
    auth: "imap",
    connectedAt: ago(11, "w"),
    lastSyncAt: ago(2, "d"),
    budget: {
      windows: [{ label: "Sends", used: 0, limit: 1000, resetsAt: ago(-10, "h") }],
    },
    stats: { chats: 58, unread: 0, sent: 143, received: 881 },
  },
  {
    id: "acc_ol_dan",
    provider: "outlook",
    handle: "dan@northwind.io",
    providerId: "dan@northwind.io",
    endUserId: "u_dan",
    status: "connected",
    auth: "oauth",
    connectedAt: ago(12, "w"),
    lastSyncAt: ago(1, "m"),
    budget: {
      windows: [{ label: "Sends", used: 61, limit: 1500, resetsAt: ago(-10, "h") }],
    },
    stats: { chats: 0, unread: 4, sent: 221, received: 1402 },
  },
  {
    id: "acc_oc_dan",
    provider: "outlook-calendar",
    handle: "dan@northwind.io",
    providerId: "dan@northwind.io",
    endUserId: "u_dan",
    status: "connected",
    auth: "oauth",
    connectedAt: ago(12, "w"),
    lastSyncAt: ago(21, "s"),
    budget: {
      windows: [{ label: "API units", used: 64, limit: 600, resetsAt: ago(-1, "h") }],
    },
    stats: { chats: 0, unread: 0, sent: 11, received: 96 },
  },
  {
    id: "acc_gm_sofia",
    provider: "gmail",
    handle: "sofia@northwind.io",
    providerId: "sofia@northwind.io",
    endUserId: "u_sofia",
    status: "connected",
    auth: "oauth",
    connectedAt: ago(21, "w"),
    lastSyncAt: ago(2, "m"),
    budget: {
      windows: [
        // Close enough to the ceiling that the dashboard holds the next send
        // rather than letting Google reject it.
        { label: "Sends", used: 1894, limit: 2000, resetsAt: ago(-6, "h") },
        { label: "Recipients", used: 8820, limit: 10000, resetsAt: ago(-6, "h") },
      ],
    },
    stats: { chats: 212, unread: 31, sent: 1894, received: 4106 },
  },
  {
    id: "acc_gn_sofia",
    provider: "google-contacts",
    handle: "sofia@northwind.io",
    providerId: "sofia@northwind.io",
    endUserId: "u_sofia",
    status: "connected",
    auth: "oauth",
    connectedAt: ago(21, "w"),
    lastSyncAt: ago(6, "m"),
    budget: {
      windows: [{ label: "API units", used: 22, limit: 600, resetsAt: ago(-1, "h") }],
    },
    stats: { chats: 0, unread: 0, sent: 0, received: 1844 },
  },
  {
    id: "acc_im_ops",
    provider: "imap",
    handle: "ops@northwind.io",
    providerId: "imap.northwind.io:993",
    endUserId: "u_ops",
    status: "connected",
    auth: "imap",
    connectedAt: ago(5, "w"),
    lastSyncAt: ago(4, "m"),
    budget: {
      windows: [{ label: "Sends", used: 22, limit: 500, resetsAt: ago(-10, "h") }],
    },
    stats: { chats: 0, unread: 9, sent: 64, received: 512 },
  },
  {
    id: "acc_ol_omar",
    provider: "outlook",
    handle: "omar.haddad@contoso.com",
    providerId: "omar.haddad@contoso.com",
    endUserId: "u_omar",
    status: "disconnected",
    auth: "oauth",
    connectedAt: ago(16, "w"),
    lastSyncAt: ago(9, "d"),
    attention: {
      code: "token_expired",
      message:
        "The refresh token was revoked at the provider, usually by a tenant administrator. Signing in again reconnects the mailbox.",
      since: ago(9, "d"),
      selfServe: true,
    },
    budget: { windows: [] },
    stats: { chats: 131, unread: 0, sent: 302, received: 2290 },
  },
];

export const END_USERS: EndUser[] = [
  {
    id: "u_maya",
    name: "Maya Rao",
    email: "maya@northwind.io",
    accountIds: ["acc_gm_maya", "acc_gc_maya"],
    createdAt: ago(18, "w"),
  },
  {
    id: "u_tom",
    name: "Tom Okafor",
    email: "tom@northwind.io",
    accountIds: ["acc_gm_tom", "acc_ic_tom"],
    createdAt: ago(14, "w"),
  },
  {
    id: "u_lena",
    name: "Lena Fischer",
    email: "lena@northwind.io",
    accountIds: ["acc_fm_lena", "acc_ol_lena"],
    createdAt: ago(7, "w"),
  },
  {
    id: "u_dan",
    name: "Dan Whitfield",
    email: "dan@northwind.io",
    accountIds: ["acc_zo_dan", "acc_ol_dan", "acc_oc_dan"],
    createdAt: ago(5, "w"),
  },
  {
    id: "u_sofia",
    name: "Sofia Marchetti",
    email: "sofia@vela.it",
    accountIds: ["acc_gm_sofia", "acc_gn_sofia"],
    createdAt: ago(11, "w"),
  },
  {
    id: "u_ops",
    name: "Northwind Ops",
    email: "ops@northwind.io",
    accountIds: ["acc_im_ops"],
    createdAt: ago(3, "w"),
  },
  {
    id: "u_omar",
    name: "Omar Haddad",
    email: "omar@sablehq.com",
    accountIds: ["acc_ol_omar"],
    createdAt: ago(16, "w"),
  },
];

export const ACCOUNT_BY_ID = new Map(ACCOUNTS.map((account) => [account.id, account]));

export const END_USER_BY_ID = new Map(END_USERS.map((user) => [user.id, user]));
